import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
/** Custom Hooks */
import useDebounce from "../../../../../app/customHooks/useDebounce";
/** Common Components */
import SwalAlert from "../../../../../services/swalService/SwalService";
import ToastService from "../../../../../services/toastService/ToastService";
import { getTeamPlanningMessageType, isTeamPlanningSuccessMessage } from "../../../../taskPlan/utils/teamPlanningResponse";
import { encryptUrlData } from "../../../../../services/CryptoService";
import CenterModel from "../../../../../components/ui/centerModel/CenterModel";
import FinalMolGrid from "../../../../../components/FinalMolGrid/FinalMolGrid";
import CardSectionSkeleton from "../../../../../components/ui/card/CardSectionSkeleton";
/** RTK Query */
import {
  useDeleteTeamPlanningMutation,
  useGetTeamPlannningListMutation,
  useUpdateTeamPlanningMutation
} from "../../../../../app/services/teamPlanningAPI";
/** Common Services & Data Files */
import { MyTeamPlanningGridConfig } from "../config/MyTeamPlanning.data";
import MyTeamMembers from "../../myTeamMembers/MyTeamMembers";

const UpcomingTeamPlanning = ({ teamIdData, searchText }) => {
  const teamId = teamIdData.teamId;
  const teamName = teamIdData.teamName;
  const planningId = teamIdData.planningId;
  const molGridRef = useRef();
  /* State*/
  const [showTeamModal, setShowTeamModal] = useState(false);
  const [TeamPlanningGridData, setTeamPlanningGridData] = useState();
  const [totalRowCount, setTotalRowCount] = useState(0);
  const debouncedSearch = useDebounce(searchText, 300);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const navigate = useNavigate();
  const location = useLocation();

  const { confirm } = SwalAlert();
  // API'S Call
  const [getTeamPlannningList, {
    isLoading: isTeamPlannningListLoading,
    isSuccess: isTeamPlannningListSuccess,
    data: isTeamPlannningListData,
  },] = useGetTeamPlannningListMutation();

  const [updateTeamPlanning, {
    isSuccess: isTeamPlanningUpdated,
    data: TeamPlanningUpdateData,
  },] = useUpdateTeamPlanningMutation();

  const [deleteTeamPlanning, {
    isSuccess: isTeamPlanningDeleted,
    data: deleteTeamPlanningData,
  },] = useDeleteTeamPlanningMutation();

  //** useEffect's */
  useEffect(() => {
    if (isTeamPlannningListSuccess && isTeamPlannningListData) {
      if (isTeamPlannningListData) {
        setTeamPlanningGridData(isTeamPlannningListData.dataSource);
      }
      if (isTeamPlannningListData.totalRecord) {
        setTotalRowCount(isTeamPlannningListData.totalRecord);
      }
    }
  }, [isTeamPlannningListSuccess, isTeamPlannningListData]);

  useEffect(() => {
    if (molGridRef.current || planningId) {
      const currentPageObject = molGridRef.current.getCurrentPageObject();
      getTeamPlanningDataList(currentPageObject);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, planningId]);

  useEffect(() => {
    if (isTeamPlanningUpdated && TeamPlanningUpdateData) {
      const message = TeamPlanningUpdateData.errorMessage;
      ToastService[getTeamPlanningMessageType(message)](message);
    }
  }, [isTeamPlanningUpdated, TeamPlanningUpdateData]);

  useEffect(() => {
    if (isTeamPlanningDeleted && deleteTeamPlanningData) {
      const message = deleteTeamPlanningData.errorMessage;
      ToastService[getTeamPlanningMessageType(message)](message);
      if (!isTeamPlanningSuccessMessage(message)) return;

      const currentPageObject = molGridRef.current.getCurrentPageObject();
      getTeamPlanningDataList(currentPageObject);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTeamPlanningDeleted, deleteTeamPlanningData]);
  
  //** Funcation's */
  const getTeamPlanningDataList = (pageObject) => {
    const request = {
      pagination: {
        pageNumber: pageObject.pageNumber,
        pageSize: pageObject.pageSize,
      },
      filters: {
        searchText: debouncedSearch,
      },
      teamId: teamId,
      isCompleted: false,
    };
    getTeamPlannningList(request);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page.pageNumber);
    setPageSize(page.pageSize);
    getTeamPlanningDataList(page);
  };


  const onNextClick = (data) => {
    let dataURL = {
      planningId: data.teamPlanningId,
      teamId: teamId,
      teamName: teamName,
      startDate: data.startDate,
      endDate: data.endDate,
      workingDays: data.workingDays,
      planningStatusId: data.planningStatusId,
      isComplete: data.isComplete
    };

    const baseUrl = location.pathname.includes('/admin/teams') ? '/admin/teams/taskplan/' : '/myteams/taskplan/';

    navigate(`${baseUrl}${encryptUrlData(dataURL)}`);
  };

  const onTeamClick = () => {
    setShowTeamModal(!showTeamModal);
  };

  const handleEditClick = (data, rowIndex) => {
    let newGridData = [...TeamPlanningGridData];
    newGridData[rowIndex] = { ...TeamPlanningGridData?.[rowIndex], ...data };

    const startDate = data.startDate ? new Date(data.startDate) : null;
    const endDate = data.endDate ? new Date(data.endDate) : null;
    const workingDays = parseInt(data.workingDays, 10);

    // Calculate the total number of days between startDate and endDate
    const totalDays =
      Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24)) + 1;

    let requestDataUpdate = {
      startDate: startDate,
      teamPlanningId: data.teamPlanningId,
      teamId: teamId,
      endDate: endDate,
      workingDays: data.workingDays,
    };
    if (startDate && endDate && startDate > endDate) {
      ToastService.error("Start date cannot be greater than end date");
      return;
    } else if (totalDays < workingDays) {
      ToastService.error(
        `The total days (${totalDays}) between the start and end dates cannot be less than the Working Days (${workingDays})`
      );
      return;
    } else if (workingDays <= 0) {
      ToastService.error("Working Days must be greater than 0");
      return;
    } else {
      updateTeamPlanning(requestDataUpdate);
    }
    setTeamPlanningGridData(newGridData);
  };

  const onDeleteClick = (data) => {
    confirm(
      "Delete?",
      "Are you sure you want to Delete?",
      "Delete",
      "Cancel"
    ).then((confirmed) => {
      if (confirmed) {
        deleteTeamPlanning(data.teamPlanningId);
      }
    });
  };

  const actionHandler = {
    EDIT: handleEditClick,
    DELETE: onDeleteClick,
    NEXT: onNextClick,
    TEAM: onTeamClick,
  };

  return (
    <div>
      <div className="team-planning center-top-space team-planning-list">
        <div className="table-grid">
          {
            isTeamPlannningListLoading ? (<CardSectionSkeleton />) :
              <FinalMolGrid
                ref={molGridRef}
                configuration={MyTeamPlanningGridConfig}
                dataSource={TeamPlanningGridData}
                allowPagination={true}
                onRowDataUpdate={handleEditClick}
                pagination={{
                  totalCount: totalRowCount,
                  pageSize: pageSize,
                  currentPage: currentPage,
                }}
                onPageChange={handlePageChange}
                onActionChange={actionHandler}
              />
          }
        </div>
      </div>
      <div>
        {showTeamModal && (
          <CenterModel
            showModal={showTeamModal}
            handleToggleModal={onTeamClick}
            modalTitle={"Choose Planning Team Members"}
            modelSizeClass="w-55"
          >
            <MyTeamMembers />
          </CenterModel>
        )}
      </div>
    </div>
  );
};

export default UpcomingTeamPlanning;
